import { useEffect } from 'react';

import { useGateValue, useStatsigClient } from '@coursera-web/statsig';

import {
  ORG_SCOPED_HOME_OVERRIDE_ORG_IDS,
  ORG_SCOPED_HOME_STATSIG_GATE,
} from 'bundles/unified-common/constants/orgScopedHomeStatsig';
import { useEnterpriseProductFeatures } from 'bundles/unified-common/contexts/EnterpriseProductFeaturesContext';
import { useOrgScopedHomeFeature } from 'bundles/unified-common/contexts/OrgScopedHomeFeatureContext';
import { useThirdPartyOrganization } from 'bundles/unified-common/contexts/ThirdPartyOrganizationContext';
import { useUserExperienceMemberships } from 'bundles/unified-common/contexts/UserExperienceMembershipsContext';
import { useThirdPartyOrganization as useOrgScopedThirdPartyOrg } from 'bundles/unified-common/contexts/org-scoped/ThirdPartyOrganizationContext';

/**
 * Determines if the org-scoped unified home experience is enabled.
 *
 * Returns true when EITHER:
 * 1. The user's current org or any program membership org is in ORG_SCOPED_HOME_OVERRIDE_ORG_IDS
 *    (bypasses gate and product feature — used for early-access partners), OR
 * 2. BOTH: the Statsig gate `org-scoped-home` passes AND `enableOrgScopedHome` product feature is enabled.
 *
 * Reads the product feature from EnterpriseProductFeaturesContext (available on enterprise/org-scoped pages)
 * or from OrgScopedHomeFeatureContext (available inside UnifiedUserExperienceHandler on consumer pages).
 */
export function useIsOrgScopedHome(): boolean {
  const userInOrgScopedRollout = useGateValue(ORG_SCOPED_HOME_STATSIG_GATE, { disableExposureLog: true });
  const { checkGate } = useStatsigClient();
  const features = useEnterpriseProductFeatures();
  const orgScopedHomeFeature = useOrgScopedHomeFeature();
  const naptimeThirdPartyOrg = useThirdPartyOrganization();
  const orgScopedThirdPartyOrg = useOrgScopedThirdPartyOrg();
  const { programMemberships } = useUserExperienceMemberships();

  const thirdPartyOrgId = orgScopedThirdPartyOrg?.id ?? naptimeThirdPartyOrg?.id;

  // Prefer EnterpriseProductFeaturesContext (enterprise pages) when available;
  // fall back to OrgScopedHomeFeatureContext (consumer pages via UnifiedUserExperienceHandler).
  const featureEnabled = features !== undefined ? features.enableOrgScopedHome ?? false : orgScopedHomeFeature.enabled;

  const isOverrideOrg =
    (!!thirdPartyOrgId && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(thirdPartyOrgId)) ||
    (programMemberships?.some((edge) => {
      const orgId = edge?.node?.program?.naptimeThirdPartyOrganization?.id;
      return orgId != null && ORG_SCOPED_HOME_OVERRIDE_ORG_IDS.has(orgId);
    }) ??
      false);

  const isEnabled = isOverrideOrg || (userInOrgScopedRollout && featureEnabled);

  // Log Statsig exposure when org-scoped home is active (for analytics tracking).
  useEffect(() => {
    if (isEnabled) {
      checkGate(ORG_SCOPED_HOME_STATSIG_GATE);
    }
  }, [isEnabled, checkGate]);

  return isEnabled;
}
